import EvaluationFlow from "../../../../flow/EvaluationFlow"
import {
  EligibleInAppMessageEligibilityFlowEvaluator,
  FrequencyInAppMessageEligibilityFlowEvaluator,
  HiddenInAppMessageEligibilityFlowEvaluator,
  PeriodInAppMessageEligibilityFlowEvaluator,
  PlatformInAppMessageEligibilityFlowEvaluator,
  TimetableInAppMessageEligibilityFlowEvaluator
} from "./InAppMessageEligibilityFlowEvaluator"
import {
  DraftInAppMessageEligibilityFlowEvaluator,
  InAppMessageEligibilityLocalFlow,
  LayoutResolveInAppMessageEligibilityFlowEvaluator,
  OverrideInAppMessageEligibilityFlowEvaluator,
  PauseInAppMessageEligibilityFlowEvaluator,
  TargetInAppMessageEligibilityFlowEvaluator
} from "./InAppMessageEligibilityLocalFlowEvaluator"
import { InAppMessageEligibilityEvaluateResult } from "../InAppMessageEligibilityEvaluateResult"
import { InAppMessageEligibilityFlowFactory } from "./InAppMessageEligibilityFlowFactory"
import { InAppMessageEligibilityLocalEvaluateRequest } from "../mode/local/InAppMessageEligibilityLocalEvaluateRequest"
import InAppMessageHiddenStorage from "../match/InAppMessageHiddenStorage"
import { InAppMessageImpressionStorage } from "../match/InAppMessageImpressionStorage"
import {
  HiddenInAppMessageMatcher,
  InAppMessageFrequencyCapMatcher,
  TargetInAppMessageMatcher,
  UserOverrideInAppMessageMatcher
} from "../match/InAppMessageMatcher"
import TargetMatcher from "../../../../match/TargetMatcher"
import { InAppMessageLayoutLocalEvaluator } from "../../layout/mode/local/InAppMessageLayoutLocalEvaluator"

export default class InAppMessageEligibilityLocalEvaluationFlowFactory
  implements InAppMessageEligibilityFlowFactory<InAppMessageEligibilityLocalEvaluateRequest>
{
  private readonly triggerFlow: InAppMessageEligibilityLocalFlow
  private readonly deliverFlow: InAppMessageEligibilityLocalFlow
  private readonly deliverReEvaluateFlow: InAppMessageEligibilityLocalFlow

  constructor(
    targetMatcher: TargetMatcher,
    inAppMessageImpressionStorage: InAppMessageImpressionStorage,
    inAppMessageHiddenStorage: InAppMessageHiddenStorage,
    layoutEvaluator: InAppMessageLayoutLocalEvaluator
  ) {
    const overrideFlow = [new OverrideInAppMessageEligibilityFlowEvaluator(new UserOverrideInAppMessageMatcher())]

    const evaluateFlow = [
      new PlatformInAppMessageEligibilityFlowEvaluator<InAppMessageEligibilityLocalEvaluateRequest>(),
      new OverrideInAppMessageEligibilityFlowEvaluator(new UserOverrideInAppMessageMatcher()),
      new DraftInAppMessageEligibilityFlowEvaluator(),
      new PauseInAppMessageEligibilityFlowEvaluator(),
      new PeriodInAppMessageEligibilityFlowEvaluator<InAppMessageEligibilityLocalEvaluateRequest>(),
      new TimetableInAppMessageEligibilityFlowEvaluator<InAppMessageEligibilityLocalEvaluateRequest>(),
      new TargetInAppMessageEligibilityFlowEvaluator(new TargetInAppMessageMatcher(targetMatcher))
    ]

    const layoutFlow = [new LayoutResolveInAppMessageEligibilityFlowEvaluator(layoutEvaluator)]

    const deduplicateFlow = [
      new FrequencyInAppMessageEligibilityFlowEvaluator<InAppMessageEligibilityLocalEvaluateRequest>(
        new InAppMessageFrequencyCapMatcher(inAppMessageImpressionStorage)
      ),
      new HiddenInAppMessageEligibilityFlowEvaluator<InAppMessageEligibilityLocalEvaluateRequest>(
        new HiddenInAppMessageMatcher(inAppMessageHiddenStorage)
      )
    ]

    const eligibleFlow = [
      new EligibleInAppMessageEligibilityFlowEvaluator<InAppMessageEligibilityLocalEvaluateRequest>()
    ]

    this.triggerFlow = EvaluationFlow.of<
      InAppMessageEligibilityLocalEvaluateRequest,
      InAppMessageEligibilityEvaluateResult
    >(...evaluateFlow, ...layoutFlow, ...deduplicateFlow, ...eligibleFlow)

    this.deliverFlow = EvaluationFlow.of<
      InAppMessageEligibilityLocalEvaluateRequest,
      InAppMessageEligibilityEvaluateResult
    >(...overrideFlow, ...deduplicateFlow, ...eligibleFlow)

    this.deliverReEvaluateFlow = EvaluationFlow.of<
      InAppMessageEligibilityLocalEvaluateRequest,
      InAppMessageEligibilityEvaluateResult
    >(...evaluateFlow, ...deduplicateFlow, ...eligibleFlow)
  }

  get(request: InAppMessageEligibilityLocalEvaluateRequest): InAppMessageEligibilityLocalFlow {
    switch (request.scope) {
      case "TRIGGER":
        return this.triggerFlow
      case "DELIVER":
        return request.inAppMessage.evaluateContext.atDeliverTime ? this.deliverReEvaluateFlow : this.deliverFlow
      default:
        throw new Error(`Unsupported scope [${request.scope}]`)
    }
  }
}
